import { gql, useMutation } from '@apollo/client';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { EmptyState } from '@ovComponents/2-component/emptyState/emptyState';
import { CompletionBox } from '@ovComponents/3-pattern/completionBox/completionBox';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentInstructionModal } from './paymentInstructionModal';

const formatCurrency = (value: number, currency = 'USD', locale = 'en-US') =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

export const TRANSITION_SCHEDULED_INCOME_FUND_TRANSFER = gql`
  mutation transitionScheduledIncomeFundTransfer(
    $input: TransitionScheduledIncomeFundTransferInput!
  ) {
    transitionScheduledIncomeFundTransfer(input: $input) {
      scheduledIncomeFundTransfer {
        id
      }
    }
  }
`;

export const PaymentInstruction = ({
  account,
  refetch,
  updateMode = false,
  editable = true,
  userId,
  fundTitle,
}: {
  account: any;
  userId: string;
  refetch?: () => void;
  updateAccount?: any;
  updateMode?: boolean;
  editable?: boolean;
  fundTitle?: string;
}) => {
  const { sys } = useThemeTokens();
  const { t } = useTranslation(['affiliationTypes', 'client']);
  const [open, setOpen] = useState<boolean>(false);
  const [action, setAction] = useState<'create' | 'edit'>('create');

  const hasScheduledIncomeFundTransfer =
    account.scheduledIncomeFundTransfer &&
    account.scheduledIncomeFundTransfer.state !== 'ARCHIVED';
  const hasCustomAmount =
    !!account.scheduledIncomeFundTransfer?.annualAmountCents &&
    account.scheduledIncomeFundTransfer?.annualAmountCents > 0;
  const totalWithdrawalAmountOption =
    account?.scheduledIncomeFundTransfer?.totalWithdrawalAmountOption ??
    undefined;

  const [transitionScheduledIncomeFundTransfer] = useMutation(
    TRANSITION_SCHEDULED_INCOME_FUND_TRANSFER,
    {
      variables: {
        input: {
          scheduledIncomeFundTransferId:
            account.scheduledIncomeFundTransfer?.id,
          transition: 'archive',
        },
      },
      onCompleted: refetch,
    },
  );

  const getWithdrawalAmountLabel = (): string => {
    const labels: Record<string, string> = {
      MINIMUM: t(
        'paymentInstruction:modal.totalAnnualWithdrawalMinimumAmountOption',
      ),
      MAXIMUM: t(
        'paymentInstruction:modal.totalAnnualWithdrawalMaximumAmountOption',
      ),
      CUSTOM: t(
        'paymentInstruction:modal.totalAnnualWithdrawalCustomAmountOption',
      ),
    };

    if (totalWithdrawalAmountOption) {
      return labels[totalWithdrawalAmountOption] ?? '';
    }

    return hasCustomAmount ? labels.CUSTOM : labels.MINIMUM;
  };

  const openInstructionModal = (actionType: 'create' | 'edit') => {
    setAction(actionType);
    setOpen(true);
  };

  if (!editable && !hasScheduledIncomeFundTransfer) {
    return (
      <Box mb={2}>
        <EmptyState>
          <Typography variant='bodyLarge'>
            {t('noLinkedAffiliate', { type: t('paymentInstruction:title') })}
          </Typography>
        </EmptyState>
      </Box>
    );
  }

  return (
    <>
      <CompletionBox
        variant={updateMode ? 'view' : 'edit'}
        state={hasScheduledIncomeFundTransfer ? 'complete' : 'todo'}
        edittable={editable}
        title={t('paymentInstruction:title')}
        onEdit={() => openInstructionModal('edit')}
        onRemove={() => transitionScheduledIncomeFundTransfer()}
        onAdd={() => openInstructionModal('create')}
      >
        {hasScheduledIncomeFundTransfer ? (
          <>
            <Typography variant='bodyMedium'>
              <span
                style={{
                  color: sys.color.onSurfaceVariant,
                  marginRight: sys.spacing.sm,
                }}
              >
                {t('paymentInstruction:totalAnnualWithdrawal')}
              </span>
              <b>{getWithdrawalAmountLabel()}</b>
            </Typography>
            {hasCustomAmount && (
              <Typography variant='bodyMedium'>
                <span
                  style={{
                    color: sys.color.onSurfaceVariant,
                    marginRight: sys.spacing.sm,
                  }}
                >
                  {t('paymentInstruction:amount')}
                </span>
                <b>
                  {formatCurrency(
                    account.scheduledIncomeFundTransfer.annualAmountCents / 100,
                  )}
                </b>
              </Typography>
            )}
            <Typography variant='bodyMedium'>
              <span
                style={{
                  color: sys.color.onSurfaceVariant,
                  marginRight: sys.spacing.sm,
                }}
              >
                {t('paymentInstruction:paymentFrequency')}
              </span>
              <b>
                {t(
                  `paymentInstruction:modal.${account.scheduledIncomeFundTransfer.frequency}`,
                )}
              </b>
            </Typography>
            <Typography variant='bodyMedium'>
              <span
                style={{
                  color: sys.color.onSurfaceVariant,
                  marginRight: sys.spacing.sm,
                }}
              >
                {t('paymentInstruction:startDate')}
              </span>
              <b>{account.scheduledIncomeFundTransfer.scheduledDate}</b>
            </Typography>
            <Typography variant='bodyMedium'>
              <span
                style={{
                  color: sys.color.onSurfaceVariant,
                  marginRight: sys.spacing.sm,
                }}
              >
                {t('paymentInstruction:depositTo')}
              </span>
              <b>{` ${account.scheduledIncomeFundTransfer.bankAccount.name} ${account.scheduledIncomeFundTransfer.bankAccount.bankAccountNumber}`}</b>
            </Typography>
          </>
        ) : (
          <Typography variant='bodyMedium'>
            {t('paymentInstruction:body', {
              fundPrefix: fundTitle ?? account.type,
            })}
          </Typography>
        )}
      </CompletionBox>
      <PaymentInstructionModal
        data-testid='payment-instruction-modal'
        account={account}
        open={open}
        setOpen={setOpen}
        userId={userId}
        refetch={refetch}
        action={action}
        fundTitle={fundTitle ?? account.type}
      />
    </>
  );
};
